import { type RestaurantBranding } from "api-types";
import { Timestamp } from "firebase/firestore";

/**
 * Document id of a restaurant's first theme. It carries the sidebar logo and
 * is the one theme the dashboard resolves by id rather than by selection.
 */
export const DEFAULT_THEME_ID = "default";

/**
 * Firestore document for a theme.
 * Stored in: restaurants-v1/{restaurantUuid}/themes/{themeId}
 *
 * A theme is a named set of branding properties that can be assigned
 * to individual MenuUrlV2 documents via their `themeId` field.
 *
 * @see ADR-011 Phase 7 for the theme support specification.
 */
export type FirestoreThemeDocument = RestaurantBranding & {
  /** Firestore document id. */
  id: string;

  /** Display name for this theme (shown in the dashboard). */
  name: string;

  /** Denormalized for potential collection group queries. */
  restaurantUuid: string;

  /** Timestamp when this theme was created. */
  createdAt: Timestamp;

  /** Timestamp when this theme was last updated. */
  updatedAt: Timestamp;
};

/**
 * Serializable theme type for use in React state.
 * Timestamps are converted to milliseconds.
 */
export type Theme = RestaurantBranding & {
  id: string;
  name: string;
  restaurantUuid: string;
  createdAtMillis: number;
  updatedAtMillis: number;
};
